import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Truck, ShieldCheck, RotateCcw, Heart } from 'lucide-react';
import { BeeLogo } from './Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let footerCache = null;

/** Site-wide storefront footer (hidden in the admin panel and on printable pages). */
export default function Footer() {
  const location = useLocation();
  const [data, setData] = useState(footerCache || { categories: [], pages: [] });

  useEffect(() => {
    if (footerCache) return;
    Promise.all([
      axios.get(`${API}/products/categories`).then(r => r.data.categories || []).catch(() => []),
      axios.get(`${API}/pages`).then(r => r.data.pages || []).catch(() => []),
    ]).then(([categories, pages]) => { footerCache = { categories, pages }; setData(footerCache); });
  }, []);

  if (location.pathname.startsWith('/admin')) return null;

  return (
    <footer className="bg-ink text-white/80 mt-0">
      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-1">
          <Link to="/" className="flex items-center gap-2 mb-4">
            <BeeLogo />
            <span className="font-black text-xl text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Market <span className="text-honey">@</span> Buzzzillion</span>
          </Link>
          <p className="text-white/60 text-sm leading-relaxed">A buzzing marketplace of useful, beautiful and fun finds, picked with care and shipped fast.</p>
        </div>
        <div>
          <p className="text-white font-bold mb-3">Shop</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:text-honey">All products</Link></li>
            <li><Link to="/sale" className="hover:text-honey">On sale</Link></li>
            {data.categories.slice(0, 6).map(c => (
              <li key={c}><Link to={`/?category=${encodeURIComponent(c)}`} className="hover:text-honey">{c}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-white font-bold mb-3">Your account</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/orders" className="hover:text-honey">Track orders</Link></li>
            <li><Link to="/wishlist" className="hover:text-honey">Favorites</Link></li>
            <li><Link to="/cart" className="hover:text-honey">Cart</Link></li>
            <li><Link to="/auth" className="hover:text-honey">Sign in / Register</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-white font-bold mb-3">Info</p>
          <ul className="space-y-2 text-sm">
            {data.pages.map(p => <li key={p.id}><Link to={`/pages/${p.slug}`} className="hover:text-honey">{p.title}</Link></li>)}
            <li className="flex items-center gap-2 text-white/60"><Truck className="w-4 h-4 text-honey" /> Fast, tracked shipping</li>
            <li className="flex items-center gap-2 text-white/60"><ShieldCheck className="w-4 h-4 text-honey" /> Secure checkout</li>
            <li className="flex items-center gap-2 text-white/60"><RotateCcw className="w-4 h-4 text-honey" /> Friendly support</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between gap-2 text-xs text-white/50">
          <span>© {new Date().getFullYear()} Market @ Buzzzillion. All rights reserved.</span>
          <span className="flex items-center gap-1">Made with <Heart className="w-3 h-3 text-coral fill-coral" /> for shoppers everywhere</span>
        </div>
      </div>
    </footer>
  );
}
